import { useState, useMemo, useEffect, useRef } from 'react';

import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import RotatableIcon from '@ui/RotatableIcon/rotatable-icon';
import Popover from '@ui/popover/popover';
import EditStateWrapper from '@ui/edit-state-wrapper/edit-state-wrapper';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import { getUserTimezone } from '@ui/calendar/utils';
import type { PopoverPositionOptions } from '@ui/popover/popover.types';
import { timezoneOptions } from './timezone-options';

type TimezoneSelectProps = {
  selectedTimezone?: string;
  onChange?: (timezone: string) => void;
  hideLabel?: boolean;
  forcePosition?: PopoverPositionOptions;
};

const TimezoneSelect = ({
  selectedTimezone,
  onChange,
  hideLabel,
  forcePosition = 'bottom-left',
}: TimezoneSelectProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const tz = useMemo(() => {
    return selectedTimezone || getUserTimezone() || 'Europe/London';
  }, [selectedTimezone]);

  const activeOption = useMemo(() => {
    if (!selectedTimezone) return null;
    return (
      timezoneOptions.find((o) => o.value === tz) ??
      timezoneOptions.find((o) => o.value === 'Europe/London') ??
      timezoneOptions[0]
    );
  }, [selectedTimezone, tz]);

  const tzLabel = activeOption?.label.replace(/^\(GMT[^)]*\)\s*/, '') ?? '';
  const tzOffset = activeOption?.label.match(/^\(GMT[^)]*\)/)?.[0] ?? '';

  const filtered = useMemo(() => {
    const query = search.toLowerCase().trim();
    if (!query) return timezoneOptions;
    return timezoneOptions.filter((o) => o.label.toLowerCase().includes(query));
  }, [search]);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        const activeEl = scrollContainerRef.current?.querySelector('[data-active="true"]');
        activeEl?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }, 100);
    }
  }, [open]);

  return (
    <EditStateWrapper
      Label={hideLabel ? undefined : 'TIMEZONE'}
      className="position-relative"
      headerClassName="position-absolute width-100-percent"
      applyTabSentinels
      hideEditLabel={!selectedTimezone}
    >
      {({ isFocusedWithin }) => (
        <div className="margin-t-24" data-show-edit-label={isFocusedWithin ? 'false' : 'true'}>
          <Popover
            closeOnClick={false}
            forcePosition={forcePosition}
            open={open}
            onClose={() => {
              setOpen(false);
              setSearch('');
            }}
            border="none"
            verticalOffset={8}
            width="fit-content"
            cycleWithArrowKeys
            containerClassName="z-11"
            renderTriggerButton={() => (
              <Button
                className="clear-button"
                onClick={() => setOpen((prev) => !prev)}
                onKeyDown={(e) => {
                  if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
                    e.preventDefault();
                    setOpen(true);
                  }
                }}
              >
                <Container
                  padding="12px"
                  className={`S3 text-left position-relative ${
                    !activeOption
                      ? 'flex-column jc-space-around'
                      : !isFocusedWithin
                        ? 'flex ai-center gap-16'
                        : 'flex-column jc-center gap-4'
                  }`}
                  borderRadius="8px"
                  width="200px"
                  height="76px"
                  background={
                    open
                      ? 'var(--global-cont-2)'
                      : activeOption && !isFocusedWithin
                        ? 'transparent'
                        : 'var(--global-cont-1)'
                  }
                  hoverBackground={
                    open ? 'var(--global-cont-2)' : activeOption ? 'transparent' : 'var(--global-cont-2)'
                  }
                >
                  <Icon
                    name={ICONS.TIMEZONE}
                    size={activeOption && isFocusedWithin ? 16 : 20}
                    color="var(--global-ico-text-3)"
                  />

                  <div className="flex-column overflow-hidden">
                    {activeOption ? (
                      <>
                        <Text className="S3" design="GlobalIcoText1" clamp={1}>
                          {tzLabel}
                        </Text>
                        <Text className="C1 margin-t-2" design="GlobalIcoText2">
                          {tzOffset}
                        </Text>
                      </>
                    ) : (
                      <Text className="C1" design="GlobalIcoText3">
                        Set timezone
                      </Text>
                    )}
                  </div>

                  {(isFocusedWithin || !activeOption) && (
                    <Container className={`position-absolute ${!activeOption ? 'bottom-14' : 'bottom-10'} right-12`}>
                      <RotatableIcon
                        name={ICONS.SELECT_DROPDOWN_CARET}
                        size={16}
                        color="var(--global-ico-text-3)"
                        rotate={open ? '180deg' : '0deg'}
                      />
                    </Container>
                  )}
                </Container>
              </Button>
            )}
          >
            {() => (
              <Container design="GlobalCont2" borderRadius="12px" className="overflow-hidden" width="300px">
                <div className="padding-8">
                  <InputWithOverlay
                    isBorderless
                    inputHeight="36px"
                    background="var(--global-cont-3)"
                    inputProps={{
                      autoFocus: true,
                      value: search,
                      onChange: (e) => setSearch(e.target.value),
                      placeholder: 'Search city or offset...',
                    }}
                  />
                </div>

                <Container
                  ref={scrollContainerRef}
                  maxHeight="240px"
                  className="no-scrollbar overflow-y-scroll padding-b-8"
                >
                  {filtered.map((opt) => {
                    const isActive = !!selectedTimezone && opt.value === tz;
                    return (
                      <Button
                        key={opt.value}
                        data-active={isActive}
                        className="clear-button width-100-percent text-left S3 padding-v-10 padding-h-16"
                        backgroundColor={isActive ? 'var(--global-cont-3)' : 'transparent'}
                        hoverBackgroundColor="var(--global-cont-3)"
                        onClick={() => {
                          onChange?.(opt.value);
                          setOpen(false);
                          setSearch('');
                        }}
                      >
                        <Text className="S3" design={isActive ? 'GlobalIcoText1' : 'GlobalIcoText2'}>
                          {opt.label}
                        </Text>
                      </Button>
                    );
                  })}
                </Container>
              </Container>
            )}
          </Popover>
        </div>
      )}
    </EditStateWrapper>
  );
};

export default TimezoneSelect;
